import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { decimal, PurchaseStatus } from '@gains-com/shared';
import {
  BalanceUpdateRTM,
  BetCreatedRTM,
  DailyBonusClaimedRTM,
  PurchaseAndPaymentUpdateRTM,
  FriendshipRemovedRTM,
  KycVerificationUpdateRTM,
  OnboardingCompleteRTM,
  RakebackClaimedRTM,
  RedemptionAndPaymentUpdateRTM,
  ReferralBonusRewardedRTM,
  PostalRequestCodeProcessedRTM,
  RealtimeSubscription,
  UserChannelRTE,
  UserFeatureUpdatedRTM,
  UserNameUpdateRTM,
  PaymentProviderAccountApprovalUpdateRTM,
  BalanceAdjustmentUpdateRTM,
  WinCreatedRTM,
  BetAndWinCreatedRTM,
  GameRoundResolvedRTM,
  BetProcessedRTM,
  BannedRTM,
  PromotionUpdateRTM,
  TopUpClaimedRTM,
  DocumentVerificationUpdateRTM,
  PlaythroughRequirementResetRTM,
  VipLevelUpRTM,
  VipLevelUpRewardClaimedRTM,
  VipWeeklyRewardClaimedRTM,
  CrmPopupRTM,
} from '@gains-com/realtime';
import { AlertService } from '@gains-com/ui-angular';

import Decimal from 'decimal.js';
import { REALTIME } from './realtime.provider';
import { UserViewState, UserViewService } from '../view-states/user-view.service';
import { UserFriendsViewService } from '../view-states/user-friends-view.service';
import { KycVerificationViewService } from '../view-states/kyc-verification-view.service.js';
import { ActivePurchaseViewService } from '../view-states/active-purchase-view.service.js';
import { MerchPurchaseViewService } from '../view-states/merch-purchase-view.service.js';
import { MerchViewService } from '../view-states/merch-view.service.js';
import { ActiveRedemptionViewService } from '../view-states/active-redemption-view.service.js';
import { AccountLimitsViewService } from '../view-states/account-limits-view.service.js';
import { DeviceModeViewService } from '../view-states/device-mode-view.service.js';
import { WalletDialogService } from '../wallet-dialog.service.js';
import { MerchDialogService } from '../merch-dialog.service.js';
import { take } from 'rxjs';
import { PaymentProviderAccountsViewService } from '../view-states/payment-provider-accounts-views.service.js';
import { UserFeatureUserViewParamKeyMap } from '../view-states/user-view.service.js';
import { PaymentMethodUpdateRTM } from '@gains-com/realtime';
import { PaymentMethodsViewService } from '../view-states/payment-methods-view.service.js';
import { PromotionsView } from '../view-states/promotions-view.service.js';
import { DocumentVerificationViewService } from '../view-states/document-verification-view.service.js';
import { VipViewService } from '../view-states/vip-view.service.js';
import { VipLevelUpNotificationService } from '../../components/vip-level-up-toast/vip-level-up-notification.service.js';
import { CrmPopupNotificationService } from '../../components/crm-popup/crm-popup-notification.service.js';
import { vipBadgeUrl } from '../../pages/vip/vip-assets.js';

@Injectable({
  providedIn: 'root',
})
export class UserRealtimeChannel {
  private readonly _realtime = inject(REALTIME);
  private readonly _userView = inject(UserViewService);
  private readonly _friendsView = inject(UserFriendsViewService);
  private readonly _kycVerificationViewService = inject(KycVerificationViewService);
  private readonly _activePurchaseViewService = inject(ActivePurchaseViewService);
  private readonly _merchPurchaseViewService = inject(MerchPurchaseViewService);
  private readonly _merchView = inject(MerchViewService);
  private readonly _activeRedemptionViewService = inject(ActiveRedemptionViewService);
  private readonly _paymentProviderAccountsViewService = inject(PaymentProviderAccountsViewService);
  private readonly _paymentMethodsViewService = inject(PaymentMethodsViewService);
  private readonly _promotionsViewService = inject(PromotionsView);
  private readonly _documentVerificationViewService = inject(DocumentVerificationViewService);
  private readonly _vipViewService = inject(VipViewService);
  private readonly _vipLevelUpNotification = inject(VipLevelUpNotificationService);
  private readonly _crmPopupNotification = inject(CrmPopupNotificationService);
  private readonly _accountLimitsView = inject(AccountLimitsViewService);
  private readonly _deviceModeView = inject(DeviceModeViewService);
  private readonly _alert = inject(AlertService);
  private readonly _walletDialog = inject(WalletDialogService);
  private readonly _merchDialog = inject(MerchDialogService);
  private readonly _snackBar = inject(MatSnackBar);

  private _subscriptionState: {
    subscription: RealtimeSubscription;
    channel: any;
    onMessageListener: any;
  } | null = null;

  async subscribe(userId: string) {
    if (this._subscriptionState) {
      this.unsubscribe();
    }

    this._subscriptionState = await this._realtime.createSubscription(`user.${userId}`, {
      [UserChannelRTE.BalanceUpdate]: (request: BalanceUpdateRTM) => this._balanceUpdated(request),
      [UserChannelRTE.BetCreated]: (request: BetCreatedRTM) => this._betCreated(request),
      [UserChannelRTE.BetProcessed]: (request: BetProcessedRTM) => this._betProcessed(request),
      [UserChannelRTE.WinCreated]: (request: WinCreatedRTM) => this._winCreated(request),
      [UserChannelRTE.BetAndWinCreated]: (request: BetAndWinCreatedRTM) => this._betAndWinCreated(request),
      [UserChannelRTE.GameRoundResolved]: (request: GameRoundResolvedRTM) => this._gameRoundResolved(request),
      [UserChannelRTE.DailyBonusClaimed]: (request: DailyBonusClaimedRTM) => this._dailyBonusClaimed(request),
      [UserChannelRTE.FriendshipRemoved]: (request: FriendshipRemovedRTM) => this._friendshipRemoved(request),
      [UserChannelRTE.RakebackClaimed]: (request: RakebackClaimedRTM) => this._rakebackClaimed(request),
      [UserChannelRTE.UserAvatarUpdate]: (update: { avatar_url: string }) => this._userAvatarUpdate(update),
      [UserChannelRTE.UserNameUpdate]: (update: UserNameUpdateRTM) => this._userNameUpdate(update),
      [UserChannelRTE.OnboardingComplete]: (request: OnboardingCompleteRTM) => this._userOnboardingComplete(request),
      [UserChannelRTE.KYCVerificationUpdate]: (request: KycVerificationUpdateRTM) =>
        this._kycVerificationUpdate(request),
      [UserChannelRTE.PurchaseAndPaymentUpdate]: (request: PurchaseAndPaymentUpdateRTM) =>
        this._purchaseAndPaymentUpdate(request),
      [UserChannelRTE.RedemptionAndPaymentUpdate]: (request: RedemptionAndPaymentUpdateRTM) =>
        this._redemptionAndPaymentUpdate(request),
      [UserChannelRTE.ReferralBonusRewarded]: (request: ReferralBonusRewardedRTM) =>
        this._referralBonusRewarded(request),
      [UserChannelRTE.PostalRequestCodeProcessed]: (request: PostalRequestCodeProcessedRTM) =>
        this._postalRequestCodeProcessed(request),
      [UserChannelRTE.UserFeatureUpdated]: (request: UserFeatureUpdatedRTM) => this._userFeatureUpdated(request),
      [UserChannelRTE.BalanceAdjustmentUpdate]: (request: BalanceAdjustmentUpdateRTM) =>
        this._balanceAdjustmentUpdate(request),
      [UserChannelRTE.PaymentProviderAccountApprovalUpdate]: (request: PaymentProviderAccountApprovalUpdateRTM) =>
        this._paymentProviderAccountApprovalUpdate(request),
      [UserChannelRTE.Banned]: (request: BannedRTM) => this._banned(request),
      [UserChannelRTE.PaymentMethodUpdate]: (request: PaymentMethodUpdateRTM) => this._paymentMethodUpdate(request),
      [UserChannelRTE.PromotionUpdate]: (request: PromotionUpdateRTM) => this._promotionUpdate(request),
      [UserChannelRTE.TopUpClaimed]: (request: TopUpClaimedRTM) => this._topUpClaimed(request),
      [UserChannelRTE.DocumentVerificationUpdate]: (request: DocumentVerificationUpdateRTM) =>
        this._documentVerificationUpdate(request),
      [UserChannelRTE.PlaythroughRequirementReset]: (request: PlaythroughRequirementResetRTM) =>
        this._playthroughRequirementReset(request),
      [UserChannelRTE.VipLevelUp]: (request: VipLevelUpRTM) => this._vipLevelUp(request),
      [UserChannelRTE.VipLevelUpRewardClaimed]: (request: VipLevelUpRewardClaimedRTM) =>
        this._vipLevelUpRewardClaimed(request),
      [UserChannelRTE.VipWeeklyRewardClaimed]: (request: VipWeeklyRewardClaimedRTM) =>
        this._vipWeeklyRewardClaimed(request),
      [UserChannelRTE.CrmPopup]: (request: CrmPopupRTM) => this._crmPopup(request),
    });

    this._subscriptionState.subscription.subscribe();
  }

  unsubscribe() {
    this._subscriptionState?.subscription?.unsubscribe();
    this._subscriptionState?.subscription.removeListener(this._subscriptionState?.onMessageListener);
    this._subscriptionState?.subscription?.dispose();
    this._subscriptionState = null;
  }

  private _paymentMethodUpdate(request: PaymentMethodUpdateRTM) {
    this._paymentMethodsViewService.updatePaymentMethod(request);
  }

  private _paymentProviderAccountApprovalUpdate(request: PaymentProviderAccountApprovalUpdateRTM) {
    this._paymentProviderAccountsViewService.updatePaymentProviderAccount(request);
  }

  private _promotionUpdate(request: PromotionUpdateRTM) {
    // Re-read rather than merging the payload: the fan-out resolves neither
    // the grants it carries nor `unexpired_promotions_count` against the
    // account's anchor mode, so merging them locally surfaces other-mode
    // promotions and flips the badge between the sweeps and merch totals.
    // GET /promotions is the anchor-scoped authority.
    if (request.update || request.remove) {
      // Swallowed: a transient failure here just leaves the view on its
      // previous data until the next sync, and realtime traffic is frequent
      // enough that surfacing every blip would be noise.
      void this._promotionsViewService.syncPromotions().catch((): void => undefined);
    }

    // Update user balances if included in the promotion reward
    if (request.sc_balance !== undefined || request.gc_balance !== undefined) {
      this._userView.updateUser({
        ...(request.sc_balance !== undefined ? { sc_balance: request.sc_balance } : {}),
        ...(request.gc_balance !== undefined ? { gc_balance: request.gc_balance } : {}),
      });
    }
  }

  private _balanceAdjustmentUpdate(request: BalanceAdjustmentUpdateRTM) {
    this._userView.updateUser({
      sc_balance: request.sc_balance,
      gc_balance: request.gc_balance,
      playthrough_requirement_id: request.playthrough_requirement_id,
      playthrough_requirement: request.playthrough_requirement,
      playthrough_progress: request.playthrough_progress,
    });
  }

  private async _userFeatureUpdated(request: UserFeatureUpdatedRTM) {
    this._userView.updateUser({
      [UserFeatureUserViewParamKeyMap[request.user_feature_type_id]]: request.enabled,
    });
  }

  private _kycVerificationUpdate(request: KycVerificationUpdateRTM) {
    this._kycVerificationViewService.updateKycVerification(request);

    this._userView.updateUser({
      kyc_verified: request.kyc_verification_status === 'approved',
    });
  }

  private _documentVerificationUpdate(request: DocumentVerificationUpdateRTM) {
    this._documentVerificationViewService.updateVerification(request);
  }

  private _purchaseAndPaymentUpdate(request: PurchaseAndPaymentUpdateRTM) {
    // Balance fields are only present when purchase succeeds
    this._userView.updateUser({
      ...(typeof request.has_had_purchase !== 'undefined' ? { has_had_purchase: request.has_had_purchase } : {}),
      ...(typeof request.has_had_card_purchase !== 'undefined'
        ? { has_had_card_purchase: request.has_had_card_purchase }
        : {}),
      ...(typeof request.has_had_merch_purchase !== 'undefined'
        ? { has_had_merch_purchase: request.has_had_merch_purchase }
        : {}),
      ...(request.gc_balance ? { gc_balance: request.gc_balance } : {}),
      ...(request.sc_balance ? { sc_balance: request.sc_balance } : {}),
      ...(request.playthrough_requirement_id ? { playthrough_requirement_id: request.playthrough_requirement_id } : {}),
      ...(request.playthrough_requirement ? { playthrough_requirement: request.playthrough_requirement } : {}),
      ...(request.playthrough_progress ? { playthrough_progress: request.playthrough_progress } : {}),
    });

    this._activePurchaseViewService.updatePurchase(request);
    this._merchPurchaseViewService.updatePurchase(request);

    if (request.purchase_status === PurchaseStatus.Complete) {
      this._accountLimitsView.fetchLifetimePurchaseLimits().catch(() => {});
      this._userView.syncPurchaseCap().catch(() => {});
    }
  }

  private _redemptionAndPaymentUpdate(request: RedemptionAndPaymentUpdateRTM) {
    this._userView.updateUser({
      gc_balance: request.gc_balance,
      sc_balance: request.sc_balance,
    });

    this._activeRedemptionViewService.updateRedemption(request);
  }

  private _postalRequestCodeProcessed(request: PostalRequestCodeProcessedRTM) {
    this._userView.updateUser({
      ...(request.sc_balance ? { sc_balance: request.sc_balance } : {}),
      ...(request.playthrough_requirement ? { playthrough_requirement: request.playthrough_requirement } : {}),
      ...(request.playthrough_requirement_id ? { playthrough_requirement_id: request.playthrough_requirement_id } : {}),
      ...(request.playthrough_progress ? { playthrough_progress: request.playthrough_progress } : {}),
    });
  }

  private _balanceUpdated(request: BalanceUpdateRTM) {
    this._userView.updateUserBalance(request);
  }

  private _userOnboardingComplete(request: OnboardingCompleteRTM) {
    this._userView.updateUser({
      username: request.user.username,
      username_normalized: request.user.username_normalized,
      country_id: request.user.country_id,
      country_name: request.user.country_name,
      country_enabled: request.user.country_enabled,
      state_id: request.user.state_id,
      state_name: request.user.state_name,
      state_enabled: request.user.state_enabled,
      dob_year: request.user.dob_year,
      dob_month: request.user.dob_month,
      dob_day: request.user.dob_day,
      pii_submitted: true,
      purchases_fiat_enabled: request.user.purchases_fiat_enabled,
      redemptions_fiat_enabled: request.user.redemptions_fiat_enabled,
      purchases_crypto_enabled: request.user.purchases_crypto_enabled,
      redemptions_crypto_enabled: request.user.redemptions_crypto_enabled,
      promotions_enabled: request.user.promotions_enabled,
      ...(request.user?.gc_balance ? { gc_balance: request.user?.gc_balance } : {}),
      ...(request.user?.sc_balance ? { sc_balance: request.user?.sc_balance } : {}),
      ...(request.playthrough_requirement?.playthrough_requirement_id
        ? {
            playthrough_requirement_id: request.playthrough_requirement.playthrough_requirement_id,
          }
        : {}),
      ...(request.playthrough_requirement?.playthrough_requirement
        ? {
            playthrough_requirement: request.playthrough_requirement.playthrough_requirement,
          }
        : {}),
    });
  }

  private _betCreated(request: BetCreatedRTM) {
    if (request.new_balance) {
      this._userView.updateUser({
        ...(request.balance_type_id === 'sweeps_coins'
          ? {
              sc_balance: request.new_balance,
              ...(!request.do_not_auto_update_balance ? { shown_balance_sc: request.new_balance } : {}),
            }
          : {
              gc_balance: request.new_balance,
              ...(!request.do_not_auto_update_balance ? { shown_balance_gc: request.new_balance } : {}),
            }),
      });

      if (
        request.balance_type_id === 'gold_coins' &&
        // GC never renders on Merch-Mode surfaces — the snackbar copy and its
        // top_up wallet view are both GC surfaces.
        !this._merchView.merchExperience() &&
        decimal(decimal(request.new_balance).toFixed(2, Decimal.ROUND_DOWN)).isZero()
      ) {
        const snackBarRef = this._snackBar.open(
          `You've run out of Gold Coins (GC). You can top up in your wallet.`,
          'Top Up',
          { panelClass: 'top-up-snackbar', duration: 10000 },
        );
        snackBarRef
          .onAction()
          .pipe(take(1))
          .subscribe(() => {
            this._walletDialog.openWalletModal({ initial_view: 'top_up' }).then();
          });
      }
    }
  }

  private _betProcessed(request: BetProcessedRTM) {
    this._userView.updateUser({
      ...(request.balance_type_id === 'sweeps_coins'
        ? {
            rakeback_sc_balance: request.rakeback_balance ?? this._userView.state?.rakeback_sc_balance,
          }
        : {
            rakeback_gc_balance: request.rakeback_balance ?? this._userView.state?.rakeback_gc_balance,
          }),
      ...(request.new_vip_xp ? { vip_xp: request.new_vip_xp } : {}),
      playthrough_progress: request.playthrough_progress,
    });
  }

  private _winCreated(request: WinCreatedRTM) {
    if (request.new_balance) {
      this._userView.updateUser({
        ...(request.balance_type_id === 'sweeps_coins'
          ? {
              sc_balance: request.new_balance,
              ...(!request.do_not_auto_update_balance ? { shown_balance_sc: request.new_balance } : {}),
            }
          : {
              gc_balance: request.new_balance,
              ...(!request.do_not_auto_update_balance ? { shown_balance_gc: request.new_balance } : {}),
            }),
      });
    }
  }

  private _betAndWinCreated(request: BetAndWinCreatedRTM) {
    if (request.new_balance) {
      this._userView.updateUser({
        ...(request.balance_type_id === 'sweeps_coins'
          ? {
              sc_balance: request.new_balance,
              ...(!request.do_not_auto_update_balance ? { shown_balance_sc: request.new_balance } : {}),
            }
          : {
              gc_balance: request.new_balance,
              ...(!request.do_not_auto_update_balance ? { shown_balance_gc: request.new_balance } : {}),
            }),
      });

      if (
        request.balance_type_id === 'gold_coins' &&
        // GC never renders on Merch-Mode surfaces — same gate as _betCreated.
        !this._merchView.merchExperience() &&
        decimal(decimal(request.new_balance).toFixed(2, Decimal.ROUND_DOWN)).isZero()
      ) {
        const snackBarRef = this._snackBar.open(
          `You've run out of Gold Coins (GC). You can top up in your wallet.`,
          'Top Up',
          { panelClass: 'top-up-snackbar' },
        );
        snackBarRef
          .onAction()
          .pipe(take(1))
          .subscribe(() => {
            this._walletDialog.openWalletModal({ initial_view: 'top_up' }).then();
          });
      }
    }
  }

  private _gameRoundResolved(request: GameRoundResolvedRTM) {
    if (request.sweeps_coin_balance_zero) {
      const update: Partial<UserViewState> = {};
      if (this._userView.state?.playthrough_progress) {
        update.playthrough_requirement_id = null;
        update.playthrough_requirement = '0';
        update.playthrough_progress = '0';
      }

      this._userView.updateUser(update);

      // delay modal popup 2 seconds so that any game session can resolve on the ui before showing they ran out of sc
      setTimeout(() => {
        // Read the mode here, not at the event: a resolution landing during the
        // delay must pick the surface. The merch experience has no gold coins to
        // buy, so its SC top-up is the shop's merch grant and the Buy Coins
        // daily cap does not gate it.
        if (this._merchView.merchExperience()) {
          void this._merchDialog.openShop();
          return;
        }
        if (this._deviceModeView.purchaseCapReached()) {
          return;
        }
        void this._walletDialog.openPurchaseModal();
      }, 2000);
    }
  }

  // The GC balance is null when the claim credited no gold coins (merch mode) —
  // only push the fields the claim actually changed.
  private _dailyBonusClaimed(request: DailyBonusClaimedRTM) {
    this._userView.updateUser({
      daily_claim_id: request.daily_claim_id,
      daily_claimed_at: request.timestamp,
      ...(request.new_gc_balance !== null ? { gc_balance: request.new_gc_balance } : {}),
      sc_balance: request.new_sc_balance,
      playthrough_requirement_id: request.new_playthrough_requirement_id,
      playthrough_requirement: request.new_playthrough_requirement_amount,
    });
  }

  // Balances are null when the claim carried no reward for that
  // currency — only push the fields the claim actually changed.
  private _vipLevelUpRewardClaimed(request: VipLevelUpRewardClaimedRTM) {
    if (request.new_gc_balance === null && request.new_sc_balance === null) {
      return;
    }

    this._userView.updateUser({
      ...(request.new_gc_balance !== null ? { gc_balance: request.new_gc_balance } : {}),
      ...(request.new_sc_balance !== null
        ? {
            sc_balance: request.new_sc_balance,
            playthrough_requirement_id: request.new_playthrough_requirement_id,
            playthrough_requirement: request.new_playthrough_requirement_amount,
          }
        : {}),
    });
  }

  // Web sibling of the native handler. Clears the weekly reward from the VIP
  // page (cross-tab / cross-device, and a same-session backstop) and patches
  // balances. Balances are null when the claim credited nothing for that
  // currency — only push the fields the claim actually changed, but always
  // clear the reward.
  private _vipWeeklyRewardClaimed(request: VipWeeklyRewardClaimedRTM) {
    this._vipViewService.weeklyRewardClaimed(request.vip_weekly_reward_id);

    if (request.new_gc_balance === null && request.new_sc_balance === null) {
      return;
    }

    this._userView.updateUser({
      ...(request.new_gc_balance !== null ? { gc_balance: request.new_gc_balance } : {}),
      ...(request.new_sc_balance !== null
        ? {
            sc_balance: request.new_sc_balance,
            playthrough_requirement_id: request.new_playthrough_requirement_id,
            playthrough_requirement: request.new_playthrough_requirement_amount,
          }
        : {}),
    });
  }

  private _friendshipRemoved(request: FriendshipRemovedRTM) {
    this._friendsView.removeFriend(request.friend_user_id);
  }

  private _rakebackClaimed(request: RakebackClaimedRTM) {
    this._userView.updateUser({
      ...(request.new_bucket.balance_type_id === 'sweeps_coins'
        ? {
            sc_balance: request.new_balance,
            shown_balance_sc: request.new_balance,
            rakeback_sc_balance: '0',
            rakeback_sc_bucket_id: request.new_bucket.rakeback_bucket_id,
          }
        : {
            gc_balance: request.new_balance,
            shown_balance_gc: request.new_balance,
            rakeback_gc_balance: '0',
            rakeback_gc_bucket_id: request.new_bucket.rakeback_bucket_id,
          }),
    });
  }

  private _userAvatarUpdate(update: { avatar_url: string }) {
    this._userView.updateUser(update);
  }

  private _userNameUpdate({ first_name, last_name }: UserNameUpdateRTM) {
    this._userView.updateUser({ first_name, last_name });
  }

  private _referralBonusRewarded(request: ReferralBonusRewardedRTM) {
    const message = request.is_referrer
      ? `Player ${request.referred_username} who you referred has met their referral bonus requirement. You have received ${request.referrer_bonus_amount_sc} SC and ${request.referrer_bonus_amount_gc} GC.`
      : `You have met your referral requirement. You have received ${request.referred_bonus_amount_sc} SC and ${request.referred_bonus_amount_gc} GC.`;

    this._userView.syncBalances().catch();

    this._alert.notifySuccess('You got a Referral Bonus!', message, {
      timeout: 0,
    });
  }

  private _banned(request: BannedRTM) {
    this._userView.updateUser({
      ...request,
    });
  }

  private _playthroughRequirementReset(_request: PlaythroughRequirementResetRTM) {
    this._userView.updateUser({
      playthrough_requirement_id: null,
      playthrough_requirement: null,
      playthrough_progress: null,
    });
  }

  private _topUpClaimed(request: TopUpClaimedRTM) {
    this._userView.updateUser({
      gc_balance: request.balance as string,
    });
  }

  private _crmPopup(_request: CrmPopupRTM) {
    // Thin signal — deliberately carries no content. Always re-pull the
    // pending list; fetchAndShow coalesces concurrent signals internally.
    void this._crmPopupNotification.fetchAndShow();
  }

  private _vipLevelUp(request: VipLevelUpRTM) {
    this._userView.updateUser({
      vip_level_config_id: request.vip_level_config_id,
      vip_title: request.vip_title,
      vip_points_required: request.vip_points_required,
      vip_rakeback_percentage: request.vip_rakeback_percentage,
      vip_daily_claim_bonus_gc: request.vip_daily_claim_bonus_gc,
      vip_daily_claim_bonus_sc: request.vip_daily_claim_bonus_sc,
      vip_next_level_config_id: request.vip_next_level_config_id,
      vip_next_title: request.vip_next_title,
      vip_next_points_required: request.vip_next_points_required,
    });

    // Pull the freshly-granted level-up reward into the VIP view state so the
    // /vip page's reward pill + My Rewards reflect it without a manual reload
    // (no-ops when the VIP page isn't open).
    this._vipViewService.refresh().catch(() => {
      /* page not open / fetch failed — nothing to surface */
    });

    // Surface the level-up toast on any page; colors + icon come from the new
    // tier's config carried on the event.
    this._vipLevelUpNotification.show({
      title: 'Level Up Reward',
      subtitle: `Claim your ${request.new_level_title} Reward`,
      color: request.vip_color || '#94ff2a',
      // Resolve the tier logo the same way the VIP page/dialogs do: prefer the
      // per-tier `logo.webp` keyed by `vip_tier_key` (the event's `vip_logo_url`
      // mirrors the deprecated, now-empty config `logo_url`, so it's blank).
      logoUrl: vipBadgeUrl({ vip_tier_key: request.vip_tier_key, logo_url: request.vip_logo_url }),
      vipLevelConfigId: request.vip_level_config_id,
    });
  }
}
